<template>
	<div :id="id" class="Message" :class="classes" data-panel="StayOpen" :aria-hidden="!show" :role="role">
		<!--title-->
		<div v-if="title" class="Message-title">
			<h5 class="Heading">
				<span v-if="icon" class="Heading-icon">
					<CloIcon :symbol="icon" />
				</span>
				<span class="Heading-main">{{ title }}</span>
			</h5>
		</div>

		<!--content-->
		<div class="Message-content">
			<!--@slot content-->
			<slot />
		</div>

		<!--actions-->
		<div v-for="(action, index) in actions" :key="index">
			<!-- if URL-->
			<div v-if="action.url">
				<a :href="action.url" class="Message-action" :class="action.classes" @click="show = false">
					<CloIcon v-if="action.icon" :symbol="action.icon" />
					<span>{{ action.text }}</span>
				</a>
			</div>

			<!-- if button-->
			<div v-else>
				<button class="Message-action" :class="action.classes" type="button" @click="show = false">
					<CloIcon v-if="action.icon" :symbol="action.icon" />
					<span>{{ action.text }}</span>
				</button>
			</div>
		</div>
	</div>
</template>

<script>
import CloHeading from '@/base/CloHeading/CloHeading.vue';
import CloIcon from '@/icons/CloIcon/CloIcon.vue';
export default {
	components: { CloHeading, CloIcon },
	name: 'CloMessage',
	data() {
		return {
			show: true,
		};
	},
	props: {
		/**
		 * unique ID automatically generated if not set
		 */
		id: {
			type: String,
			default: 'id-' + Math.random().toString(36).substr(2, 9),
		},
		/**
		 * Title in the message header
		 */
		title: {
			type: String,
			default: null,
		},
		/**
		 * Icon next to the title
		 */
		icon: {
			type: String,
			default: null,
		},
		/**
		 * A list of links or buttons in the message footer
		 */
		actions: {
			type: Array,
			default() {
				return [{ text: 'Dismiss', icon: 'close' }];
			},
		},
		/**
		 * This controls the size of the message bar. Either small, medium or large
		 */
		size: {
			type: String,
			default: null,
		},
		/**
		 * The color style: info, reverse, success, alert, or error
		 */
		state: {
			type: String,
			default: null,
		},
		/**
		 * Style vertically
		 */
		vertical: {
			type: Boolean,
			default: false,
		},
		/**
		 * Positioned as an overlay
		 */
		floating: {
			type: Boolean,
			default: false,
		},
		/**
		 * The a11y role of the message: status, alert, alert-dialog
		 */
		role: {
			type: String,
			default: 'status',
		},
		/**
		 * increment this to show message again
		 */
		showAgain: {
			type: Number,
			default: 0,
		},
	},
	watch: {
		showAgain() {
			show = true;
		},
	},
	computed: {
		classes() {
			let classes = [];
			const prefix = 'Message--';
			if (this.size) {
				classes.push(prefix + this.size);
			}
			if (this.state) {
				classes.push(prefix + this.state);
			}
			if (this.vertical) {
				classes.push(prefix + 'vertical');
			}
			if (this.floating) {
				classes.push(prefix + 'floating');
			}
			return classes.join(' ');
		},
	},
};
</script>

<style lang="scss">
.Message {
	position: relative;
	overflow: hidden;
	display: flex;
	@include text(3);
	@include stack-md;
	background-color: $color-highlight;
	width: 100%;
	&[aria-hidden='true'],
	&[hidden] {
		display: none;
	}
}

// Title (optional)
.Message-title {
	@include inset-md;
	border-right: 1px solid $color-white;
	[dir='rtl'] & {
		border-left: 1px solid $color-white;
		border-right: 0;
	}
	display: flex;
	align-items: center;
	.Heading {
		margin: 0;
	}
}

// Content (required)
.Message-content {
	@include inset-md;
	flex-grow: 1;
	> * {
		&:last-child {
			margin-bottom: 0;
		}
	}
	// Vertically center content (useful when title or action is larger)
	&--middle {
		display: flex;
		align-items: center;
	}
}

// Action (optional) - can be a button or a link
.Message-action {
	@include inset-md;
	display: flex;
	align-items: center;
	line-height: 1;
	flex-shrink: 0;
	color: $color-link;
	&:hover,
	&:active,
	&:focus {
		color: $color-link-hover;
	}
	&--neutral {
		color: $color-text-muted;
		&:hover,
		&:active,
		&:focus {
			color: $color-text;
		}
	}
	.Icon {
		@include icon-sm;
	}
	> * {
		&:not(:last-child) {
			@include inline-xs;
		}
	}
	// Remove bottom margin from buttons inside of action
	.Button {
		margin-bottom: 0;
	}
}

/* --------------------------------
	Variants
-------------------------------- */

// Small variant
.Message--sm,
.Message--small, // --small maintained for backwards compatibility
.Message--small-text {
	// --small-text maintained for backwards compatibility
	@include text(2);
	.Message-title,
	.Message-content,
	.Message-action {
		@include inset-sm;
	}
}

// Large variant
.Message--lg {
	@include stack-lg;
	@include text(3);
	.Message-title,
	.Message-content,
	.Message-action {
		@include inset-lg;
	}
}

// Vertical variants
@mixin messageVertical {
	flex-wrap: wrap;
	justify-content: flex-end;
	.Message-title,
	.Message-content {
		flex-grow: 1;
		flex-basis: 100%;
	}
	.Message-title {
		border-right: none;
		border-left: none;
		border-bottom: 1px solid $color-white;
	}
}

// Vertical variant - vertical at all screen sizes
.Message--vertical {
	@include messageVertical;
}

// Small vertical variant - vertical at small screen sizes only
.Message--sm-vertical {
	@include breakpoint(mediumsmall, down) {
		@include messageVertical;
	}
}

// Success variant
.Message--info {
	background-color: $color-primary-1;
	.Message-title {
		background-color: $color-primary-3;
		border: none;
		.Heading {
			color: $color-white;
		}
	}
}

// Success variant
.Message--success {
	background-color: $color-secondary-1;
	.Message-title {
		background-color: $color-secondary-3;
		border: none;
		.Heading {
			color: $color-white;
		}
	}
}

// Alert variants
.Message--alert {
	background-color: desaturate(mix(#fff, $color-alert-3, 86%), 10);
	.Message-title {
		background-color: $color-alert-3;
		border: none;
		.Heading {
			color: $color-white;
		}
	}
}

// Error variant
.Message--error {
	background-color: desaturate(mix(#fff, $color-error-3, 86%), 10);
	.Message-title {
		background-color: $color-error-3;
		border: none;
		.Heading {
			color: $color-white;
		}
	}
}

// Reverse variant - for dark backgrounds;
.Message--reverse {
	background-color: $color-black;
	color: $color-text-reverse;
	.Message-title {
		border-color: $color-neutral-5;
		.Heading {
			color: $color-white;
		}
	}
	.Message-content {
		a {
			color: $color-link-reverse;
			&:hover,
			&:active,
			&:focus {
				color: $color-link-hover-reverse;
			}
		}
	}
	.Message-action {
		color: $color-link-reverse;
		&:hover,
		&:active,
		&:focus {
			color: $color-link-hover-reverse;
		}
		&--neutral {
			color: $color-text-muted-reverse;
			&:hover,
			&:active,
			&:focus {
				color: $color-text-reverse;
			}
		}
	}
	&.Message--info {
		background-color: $color-primary-5;
	}
	&.Message--success {
		background-color: $color-secondary-5;
	}
	&.Message--alert {
		background-color: darken($color-alert-3, 30);
	}
	&.Message--error {
		background-color: darken($color-error-3, 20);
	}
}

// Floating variants
@mixin messageFloating {
	position: absolute;
	margin: $space-md;
	width: auto;
	width: max-content;
	max-width: calc(100% - 2rem);
	border-radius: $space-sm;
	@include dropShadow(2);
	z-index: 10;
}
// Default position is top
.Message--floating,
.Message--floatingTop {
	@include messageFloating;
	top: 0;
	left: calc(50% - 1rem);
	transform: translateX(-50%);
	[dir='rtl'] & {
		left: 0;
		right: calc(50% - 1rem);
		transform: translateX(50%);
	}
}
.Message--floatingBottom {
	@include messageFloating;
	bottom: 0;
	left: calc(50% - 1rem);
	transform: translateX(-50%);
	[dir='rtl'] & {
		left: 0;
		right: calc(50% - 1rem);
		transform: translateX(50%);
	}
}
.Message--floatingTopLeft {
	@include messageFloating;
	@include u-topLeft;
}
.Message--floatingTopRight {
	@include messageFloating;
	@include u-topRight;
}
.Message--floatingBottomLeft {
	@include messageFloating;
	@include u-bottomLeft;
}
.Message--floatingBottomRight {
	@include messageFloating;
	@include u-bottomRight;
}

// Inline variant
.Message--inline {
	display: inline-flex;
	width: auto;
	border-radius: $radius-sm;
}
</style>
